/**
 * Shared layout shell for Second / Third Temple product-page heroes.
 * Page-specific colors, media, and GSAP refs stay in each page.
 *
 * Mobile (from bmikdash.com screenshots):
 * - Colored card matches navbar width (--header-width) with a small gap under the header
 * - Card height ~55vh; temple image is NOT clipped — it breaks out below the card
 *   so the card’s bottom edge crosses the middle of the temple
 * - Media sits above the card (z-index) with overflow: visible
 */

/** Page wrapper layout bits (merge with page backgroundColor). */
export const templePageShellSx = {
    minHeight: '100vh',
    /* Pull up through App main padding so the hero can bleed under the fixed header */
    marginTop: 'calc(-1 * var(--page-header-offset))',
    pt: 0,
    pb: 8,
    position: 'relative',
    zIndex: 1,
    width: '100%',
    overflowX: 'clip',
    overflowY: 'visible',
};

/**
 * Hero section.
 * Mobile: in-flow media under the header; card is an absolute backdrop.
 * Desktop: full-viewport min-height; media frame applies nav clearance.
 */
export const templeHeroSectionSx = {
    position: 'relative',
    zIndex: 2,
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: { xs: 'flex-start', md: 'center' },
    alignItems: 'center',
    boxSizing: 'border-box',
    /* Mobile: allow temple to break out below the card; fit first screen with CTA */
    overflow: { xs: 'visible', md: 'hidden' },
    pt: {
        xs: 'calc(var(--header-height) + var(--temple-hero-mobile-gap))',
        md: 0,
    },
    /* Mobile: height from content. Desktop: definite 100vh so flex:1 media frame does not collapse to 0. */
    minHeight: {
        xs: '100vh',
        md: '100vh',
    },
    height: { xs: 'auto', md: '100vh' },
    '@supports (min-height: 100dvh)': {
        minHeight: { xs: '100dvh', md: '100dvh' },
    },
    '@supports (height: 100dvh)': {
        '@media (min-width: 900px)': {
            height: '100dvh',
            minHeight: '100dvh',
        },
    },
};

/**
 * Mobile background rectangle — same left/right edges as the navbar.
 * Backdrop only: does not clip the temple image.
 */
export const templeMobileHeroBgSx = {
    position: 'absolute',
    top: 'calc(var(--header-height) + var(--temple-hero-mobile-gap))',
    left: 'calc((100% - var(--temple-hero-mobile-width)) / 2)',
    width: 'var(--temple-hero-mobile-width)',
    height: 'var(--temple-hero-mobile-height)',
    borderRadius: 'var(--temple-hero-radius)',
    boxSizing: 'border-box',
    transformOrigin: 'center center',
    backdropFilter: 'blur(10px)',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
    zIndex: 0,
    display: { xs: 'block', md: 'none' },
    pointerEvents: 'none',
};

/**
 * Desktop media frame — always full width of the hero.
 * Aspect ratio must never set this container's width.
 */
export const templeDesktopHeroFrameSx = {
    display: { xs: 'none', md: 'flex' },
    justifyContent: 'center',
    alignItems: 'center',
    flex: 1,
    width: '100%',
    /* Fill the definite section height; avoid minHeight:0 alone (collapses absolute media) */
    minHeight: 0,
    height: '100%',
    alignSelf: 'stretch',
    pt: 'var(--temple-hero-nav-clearance)',
    px: 'var(--temple-hero-inset)',
    pb: 'var(--temple-hero-inset)',
    position: 'relative',
    overflow: 'hidden',
    boxSizing: 'border-box',
};

/**
 * Inner media rectangle (video / cream panel) — desktop.
 * clip-path inset keeps corners rounded even when GSAP applies transforms to children.
 */
export const templeHeroMediaRectSx = {
    position: 'relative',
    width: '100%',
    height: '100%',
    borderRadius: 'var(--temple-hero-radius)',
    overflow: 'hidden',
    clipPath: 'inset(0 round var(--temple-hero-radius))',
    WebkitClipPath: 'inset(0 round var(--temple-hero-radius))',
};

/**
 * Mobile logo / media stack — same width as the card, in normal flow.
 * overflow: visible so the temple can break out below the colored card.
 */
export const templeMobileHeroMediaSx = {
    position: 'relative',
    zIndex: 1,
    width: 'var(--temple-hero-mobile-width)',
    maxWidth: 'var(--temple-hero-mobile-width)',
    mx: 'auto',
    display: { xs: 'flex', md: 'none' },
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'center',
    boxSizing: 'border-box',
    overflow: 'visible',
    transformOrigin: 'center center',
};

/**
 * Second Temple mobile hero graphic (composite logo + title + temple).
 * Full card width; height auto so the lower temple hangs below the card.
 */
export const templeMobileHeroFillSx = {
    display: 'block',
    width: '100%',
    height: 'auto',
    maxWidth: '100%',
    maxHeight: 'none',
    objectFit: 'contain',
    objectPosition: 'center top',
    flexShrink: 0,
    pointerEvents: 'none',
};
